import {
  getVoucherPaymentReceipt,
  getVoucherTypes,
  initiateVoucherOrderPayment,
  Vouchers,
  VoucherPaymentReceipt,
  VoucherOrderStatus,
} from "@/services/core-api-adapter";
import { assign, fromPromise, setup } from "xstate";

const MAX_POLLING_TIME_IN_SECONDS =
  Number(import.meta.env.VITE_APP_VOUCHER_MAX_POLLING_TIME) || 180;
const POLLING_INTERVAL_IN_MILLISECONDS =
  Number(import.meta.env.VITE_APP_VOUCHER_POLLING_INTERVAL) || 5000;

interface Context {
  vouchers: Vouchers | undefined;
  selectedVoucherType: any;
  selectedVoucherSubType: any;
  initiatePaymentResponse: any;
  paymentStartTime: Date | null;
  paymentReceiptResponse: VoucherPaymentReceipt | null;
}

const initialContextValues: Context = {
  vouchers: undefined,
  selectedVoucherType: null,
  selectedVoucherSubType: null,
  initiatePaymentResponse: null,
  paymentStartTime: null,
  paymentReceiptResponse: null,
};

export const eventNames = {
  BUY_VOUCHER_CLICKED: "BUY_VOUCHER_CLICKED",
  USE_VOUCHER_CLICKED: "USE_VOUCHER_CLICKED",
  DOCTOR_NURSE_CONSULTATION_VOUCHER_CLICKED:
    "DOCTOR_NURSE_CONSULTATION_VOUCHER_CLICKED",
  PATHOLOGY_VOUCHER_CLICKED: "PATHOLOGY_VOUCHER_CLICKED",
  BACK_BUTTON_CLICKED: "BACK_BUTTON_CLICKED",
  GO_HOME_BUTTON_CLICKED: "GO_HOME_BUTTON_CLICKED",
  USER_REDIRECTED: "USER_REDIRECTED",
  CONTINUE_BUTTON_CLICKED: "CONTINUE_BUTTON_CLICKED",
  DOCTOR_VOUCHER_CLICKED: "DOCTOR_VOUCHER_CLICKED",
  NURSE_VOUCHER_CLICKED: "NURSE_VOUCHER_CLICKED",
  PAYMENT_SUCCESS_RECEIVED: "PAYMENT_SUCCESS_RECEIVED",
  VIEW_RECEIPT_BUTTON_CLICKED: "VIEW_RECEIPT_BUTTON_CLICKED",
};

export const voucheringFlowStateMachine = setup({
  types: {
    context: {} as Context,
  },
  actions: {
    addVoucherTypesToContext: assign(({ event }) => {
      return {
        vouchers: event.output,
      };
    }),
    addSelectedVoucherTypeToContext: assign(({ event }) => {
      return {
        selectedVoucherType: event.input,
      };
    }),
    addVoucherSubTypeToContext: assign(({ event }) => {
      return {
        selectedVoucherSubType: event.input,
      };
    }),
    addInitiatePaymentResponseToContext: assign(({ event }) => {
      return {
        initiatePaymentResponse: event.output,
      };
    }),
    addPaymentStartTimeToContext: assign(() => {
      return {
        paymentStartTime: new Date(),
      };
    }),
    addPaymentReceiptResponseToContext: assign(({ event }) => {
      return {
        paymentReceiptResponse: event.output,
      };
    }),
  },
  actors: {
    getVoucherTypes: fromPromise(async () => {
      return getVoucherTypes();
    }),

    initiatePayment: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const voucherVendorDefinitionId =
          input?.context?.selectedVoucherSubType?.voucherVendorDefinitionId ||
          "";

        //TO-DO: add the voucherCode from the prompt screen here if it exists

        return initiateVoucherOrderPayment(voucherVendorDefinitionId);
      }
    ),

    fetchPaymentReceipt: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return getVoucherPaymentReceipt(
          input?.context?.initiatePaymentResponse?.voucherOrderId || ""
        );
      }
    ),
  },
  guards: {
    timeoutExceeded: function ({ context }) {
      const currentDate = new Date();
      const paymentStartTime = context.paymentStartTime;

      if (paymentStartTime) {
        return (
          Number(currentDate.getTime() - paymentStartTime.getTime()) / 1000 >
          MAX_POLLING_TIME_IN_SECONDS
        );
      }

      return false;
    },
    userHasSuccessfulPayment: function ({ context }) {
      const isPaymentSuccessful = Boolean(
        context.paymentReceiptResponse?.paymentSuccess
      );

      return isPaymentSuccessful;
    },
    userHasUnsuccessfulPayment: function ({ context }) {
      return (
        context.paymentReceiptResponse?.status ===
        VoucherOrderStatus.PAYMENT_FAILED
      );
    },
  },
  delays: {
    POLLING_INTERVAL: POLLING_INTERVAL_IN_MILLISECONDS,
  },
}).createMachine({
  context: initialContextValues,
  id: "voucheringFlow",
  initial: "fetchingVoucherTypes",
  states: {
    fetchingVoucherTypes: {
      invoke: {
        src: "getVoucherTypes",
        onDone: {
          target: "buyOrUseVoucher",
          actions: "addVoucherTypesToContext",
        },
        onError: {
          target: "error",
        },
      },
    },
    buyOrUseVoucher: {
      on: {
        [eventNames.DOCTOR_NURSE_CONSULTATION_VOUCHER_CLICKED]: {
          target: "doctorNurseConsultSelection",
          actions: "addSelectedVoucherTypeToContext",
        },
        [eventNames.PATHOLOGY_VOUCHER_CLICKED]: {
          target: "pathologyTestSelection",
          actions: "addSelectedVoucherTypeToContext",
        },
      },
    },
    doctorNurseConsultSelection: {
      on: {
        [eventNames.DOCTOR_VOUCHER_CLICKED]: {
          target: "showingPrompt",
          actions: "addVoucherSubTypeToContext",
        },
        [eventNames.NURSE_VOUCHER_CLICKED]: {
          target: "showingPrompt",
          actions: "addVoucherSubTypeToContext",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "buyOrUseVoucher",
        },
      },
    },
    showingPrompt: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "initiatingPayment",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "doctorNurseConsultSelection",
        },
      },
    },
    initiatingPayment: {
      invoke: {
        input: ({ context }) => ({ context }),
        onDone: {
          target: "breakoutScreen",
          actions: {
            type: "addInitiatePaymentResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "initiatePayment",
      },
    },
    breakoutScreen: {
      on: {
        [eventNames.USER_REDIRECTED]: {
          target: "waitingForPaymentOutcome",
          actions: {
            type: "addPaymentStartTimeToContext",
          },
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingPrompt",
        },
      },
    },
    waitingForPaymentOutcome: {
      after: {
        POLLING_INTERVAL: {
          target: "fetchingPaymentReceipt",
        },
      },
    },
    fetchingPaymentReceipt: {
      invoke: {
        id: "fetchingPaymentReceipt",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "processingPaymentOutcome",
          actions: {
            type: "addPaymentReceiptResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "fetchPaymentReceipt",
      },
    },
    processingPaymentOutcome: {
      always: [
        {
          target: "exit",
          guard: {
            type: "timeoutExceeded",
          },
        },
        {
          target: "showingOrderSummary",
          guard: {
            type: "userHasSuccessfulPayment",
          },
        },
        {
          target: "paymentFailed",
          guard: {
            type: "userHasUnsuccessfulPayment",
          },
        },
        {
          target: "waitingForPaymentOutcome",
        },
      ],
    },
    showingOrderSummary: {
      on: {
        [eventNames.VIEW_RECEIPT_BUTTON_CLICKED]: {
          target: "showingPaymentReceipt",
        },
      },
    },
    showingPaymentReceipt: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingOrderSummary",
        },
      },
    },
    paymentFailed: {
      on: {
        GO_HOME_BUTTON_CLICKED: {
          target: "exit",
        },
      },
    },
    pathologyTestSelection: {},
    exit: {
      type: "final",
    },
    error: {
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
  },
});
